/**
 * Maintenance Mode Hook
 * 
 * Manages application-wide maintenance mode state. When maintenance mode is enabled,
 * the application displays a maintenance banner and may restrict certain operations.
 * This is useful for planned system updates, migrations, or infrastructure changes.
 * 
 * **Key Features:**
 * - Check if maintenance mode is currently active
 * - Admin users can bypass maintenance restrictions
 * - Organization-level and user-level maintenance control
 * - Loading state for async feature flag checks
 * 
 * **Maintenance Mode Behavior:**
 * - Display maintenance banner to all users
 * - Optionally restrict write operations
 * - Admins retain full access for monitoring
 * - Gracefully handle ongoing operations
 * 
 * **Admin Bypass:**
 * - Users with `role === 'admin'` automatically bypass maintenance
 * - Allows administrators to monitor and manage during maintenance
 * - Useful for testing and validation before general availability
 * 
 * **Use Cases:**
 * - Scheduled database migrations
 * - System upgrades and deployments
 * - Emergency maintenance windows
 * - Testing with limited user access
 * 
 * @returns {Object} Maintenance mode state
 * @returns {boolean} isInMaintenance - Whether maintenance mode is active
 * @returns {boolean} canBypassMaintenance - Whether current user can bypass
 * @returns {boolean} isLoading - Whether maintenance state is being checked
 * 
 * @example
 * // Display maintenance banner
 * const { isInMaintenance, canBypassMaintenance, isLoading } = useMaintenanceMode();
 * 
 * if (isLoading) return <Spinner />;
 * 
 * if (isInMaintenance && !canBypassMaintenance) {
 *   return <MaintenanceBanner />;
 * }
 * 
 * @example
 * // Conditionally restrict operations
 * const { isInMaintenance, canBypassMaintenance } = useMaintenanceMode();
 * const isRestricted = isInMaintenance && !canBypassMaintenance;
 * 
 * <Button disabled={isRestricted}>
 *   {isRestricted ? 'Unavailable (Maintenance)' : 'Submit'}
 * </Button>
 */

import { useState, useEffect } from 'react';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';
import { useAuth } from '@/lib/auth';

export const useMaintenanceMode = () => {
  const [isInMaintenance, setIsInMaintenance] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user, organization } = useAuth();

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const enabled = await featureFlags.isEnabled(
          FEATURE_FLAGS.MAINTENANCE_MODE,
          user?.id,
          organization?.id
        );
        setIsInMaintenance(enabled);
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
        setIsInMaintenance(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenanceMode();
  }, [user, organization]);

  const canBypassMaintenance = user?.role === 'admin';

  return { isInMaintenance, canBypassMaintenance, isLoading };
};
